import React, { useEffect, useRef, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useCloseOnEscape } from './closeOnEscape';
import { useMenuHeight } from './menuPlacement';
import { pillShape } from './MultiSelectPill';
import './FilterPill.css';

const optionCountBeforeSearchBox = 8;

const valueWidth = { maxWidth: '14rem' };
const menuWidth = { minWidth: '15rem', maxWidth: 'min(24rem, calc(100vw - 2rem))' };

export interface SelectPillOption {
  value: string;
  label: string;
}

export interface SelectPillProps {
  id: string;
  label: string;
  icon?: string;
  value?: string;
  options: SelectPillOption[];
  onChange: (value?: string) => void;
}

export default function SelectPill(props: SelectPillProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const pillRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { maxHeight, measure } = useMenuHeight(pillRef, open);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const show = () => {
    measure();
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  useCloseOnEscape(pillRef, open, close);

  const selected = props.options.find(option => option.value === props.value);
  const searchable = props.options.length > optionCountBeforeSearchBox;
  const noun = props.label.toLowerCase();
  const visible = query
    ? props.options.filter(option => option.label.toLowerCase().includes(query.toLowerCase()))
    : props.options;

  const choose = (value: string) => {
    props.onChange(value);
    close();
  };

  return (
    <div className="d-inline-flex align-items-stretch mw-100" ref={pillRef}>
      <Dropdown show={open} onToggle={next => next ? show() : close()} autoClose="outside">
        <Dropdown.Toggle
          as="button"
          type="button"
          id={`${props.id}-toggle`}
          className={`filter-pill d-inline-flex align-items-center text-nowrap border bg-white${selected ? ' active filter-pill-clearable' : ''}`}
          style={pillShape}>
          {props.icon && <i className={`bi-${props.icon} me-2`} aria-hidden="true" />}
          <span className="fw-semibold text-truncate" style={valueWidth}>{selected?.label ?? props.label}</span>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="filter-pill-menu pt-1 shadow-lg overflow-hidden"
          style={{ ...menuWidth, maxHeight }}
          popperConfig={{ modifiers: [{ name: 'preventOverflow', options: { padding: 8 } }] }}>
          {searchable &&
            <input
              type="text"
              className="form-control form-control-sm mx-2 mt-1 mb-2 flex-shrink-0"
              style={{ width: 'calc(100% - 1rem)' }}
              placeholder={`Search ${noun}…`}
              ref={searchRef}
              value={query}
              onChange={e => setQuery(e.target.value)} />}
          <div className="overflow-auto flex-grow-1" style={{ minHeight: 0 }}>
            {visible.map(option =>
              <Dropdown.Item
                key={option.value}
                as="button"
                type="button"
                role="menuitemradio"
                aria-checked={option.value === props.value}
                className={`text-truncate${option.value === props.value ? ' active' : ''}`}
                title={option.label}
                onClick={() => choose(option.value)}>
                {option.label}
              </Dropdown.Item>)}
            {visible.length === 0 &&
              <div className="dropdown-item-text text-muted small">No matches</div>}
          </div>
        </Dropdown.Menu>
      </Dropdown>
      {selected &&
        <button
          type="button"
          className="filter-pill-clear"
          aria-label={`Clear ${props.label}`}
          onClick={() => props.onChange(undefined)}>
          <i className="bi-x-lg" aria-hidden="true" />
        </button>}
    </div>
  );
}
